// src/admin/pages/AdminAddProduct.js
import React, { useState, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "../AdminSidebar";
import AdminHeader from "../AdminHeader";
import { FaSave, FaArrowLeft, FaImage, FaPlus, FaTrash } from "react-icons/fa";
import { AdminLanguageContext } from "../context/AdminLanguageContext";
import {
  createAdminProduct,
  getAdminCategories,
  getAdminTags,
  createAdminTag,
} from "../../services/adminProducts.api";
import { uploadAdminFile } from "../../services/adminUpload.api";
import { FaBox, FaSearch } from "react-icons/fa";
import { useRef } from "react";
import { FiChevronRight } from "react-icons/fi";
import { checkAdminSlug } from "../../services/adminProducts.api";
import { FaCheck } from "react-icons/fa";
import { FaPalette } from "react-icons/fa";
import { FiAlertCircle } from "react-icons/fi";
import {
  TagIcon as TagOutline,
} from "@heroicons/react/24/outline";

import {
  TagIcon as TagSolid,
} from "@heroicons/react/24/solid";

const shakeClass = "animate-[shake_0.4s_ease-in-out]";

const shakeKeyframes = `
@keyframes shake {
  0% { transform: translateX(0); }
  25% { transform: translateX(-4px); }
  50% { transform: translateX(4px); }
  75% { transform: translateX(-4px); }
  100% { transform: translateX(0); }
}
`;

const FieldLabel = ({ label, hint, error, valid }) => {
  return (
    <div className="group relative flex items-center gap-1">

      {/* LABEL */}
      <span className="text-[12px] font-medium text-gray-900">
        {label}
      </span>

      {/* STATUS ICON (✔ or ?) */}
      <div
        className={`
          w-[18px] h-[18px] rounded-full flex items-center justify-center
          text-[11px] font-bold
          transition cursor-pointer
          ${valid
            ? "bg-[#e3f1df] text-[#2e7d32]"
            : error
            ? "bg-[#fdecea] text-[#d72c0d]"
            : "bg-[#f1f2f3] text-[#6d7175] hover:bg-[#e1e3e5]"}
        `}
      >
        {valid ? <FaCheck className="text-[10px]" /> : "?"}
      </div>

      {/* TOOLTIP */}
      <div
        className="
          absolute bottom-full mb-2 left-0
          hidden group-hover:block group-focus-within:block
          z-50 w-[240px]
          bg-white text-[#202223] text-[12px]
          border border-[#e1e3e5]
          rounded-lg px-3 py-2
          shadow-[0_8px_20px_rgba(0,0,0,0.08)]
        "
      >
        {error ? (
          <span className="text-[#d72c0d]">{error}</span>
        ) : (
          hint
        )}
      </div>
    </div>
  );
};

const slugify = (text) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "") // remove special chars
    .replace(/\s+/g, "-")         // spaces → -
    .replace(/-+/g, "-");         // remove duplicate -
};

const COLOR_PALETTE = [
  // NEUTRALS
  { name: "Black", hex: "#000000" },
  { name: "White", hex: "#FFFFFF" },
  { name: "Gray", hex: "#808080" },
  { name: "Light Gray", hex: "#D3D3D3" },
  { name: "Dark Gray", hex: "#4F4F4F" },
  { name: "Charcoal", hex: "#36454F" },
  { name: "Silver", hex: "#C0C0C0" },
  { name: "Smoke", hex: "#738276" },
  { name: "Ash", hex: "#B2BEB5" },
  { name: "Ivory", hex: "#FFFFF0" },

  // REDS
  { name: "Red", hex: "#FF0000" },
  { name: "Dark Red", hex: "#8B0000" },
  { name: "Crimson", hex: "#DC143C" },
  { name: "Scarlet", hex: "#FF2400" },
  { name: "Burgundy", hex: "#800020" },
  { name: "Wine", hex: "#722F37" },
  { name: "Rose Red", hex: "#C21E56" },
  { name: "Cherry", hex: "#DE3163" },
  { name: "Ruby", hex: "#E0115F" },
  { name: "Coral Red", hex: "#FF4040" },

  // PINKS
  { name: "Pink", hex: "#FFC0CB" },
  { name: "Hot Pink", hex: "#FF69B4" },
  { name: "Baby Pink", hex: "#F4C2C2" },
  { name: "Blush", hex: "#DE5D83" },
  { name: "Rose", hex: "#FF007F" },
  { name: "Fuchsia", hex: "#FF00FF" },
  { name: "Magenta", hex: "#FF0090" },
  { name: "Dusty Pink", hex: "#DCAE96" },
  { name: "Peach Pink", hex: "#FFDAB9" },
  { name: "Salmon", hex: "#FA8072" },

  // BLUES
  { name: "Blue", hex: "#0000FF" },
  { name: "Navy", hex: "#001F3F" },
  { name: "Royal Blue", hex: "#4169E1" },
  { name: "Sky Blue", hex: "#87CEEB" },
  { name: "Baby Blue", hex: "#89CFF0" },
  { name: "Steel Blue", hex: "#4682B4" },
  { name: "Denim", hex: "#1560BD" },
  { name: "Cobalt", hex: "#0047AB" },
  { name: "Ocean", hex: "#4F42B5" },
  { name: "Teal Blue", hex: "#367588" },

  // GREENS
  { name: "Green", hex: "#00A651" },
  { name: "Dark Green", hex: "#006400" },
  { name: "Olive", hex: "#808000" },
  { name: "Mint", hex: "#98FF98" },
  { name: "Emerald", hex: "#50C878" },
  { name: "Lime", hex: "#00FF00" },
  { name: "Forest", hex: "#228B22" },
  { name: "Moss", hex: "#8A9A5B" },
  { name: "Sage", hex: "#9CAF88" },
  { name: "Sea Green", hex: "#2E8B57" },

  // YELLOWS
  { name: "Yellow", hex: "#FFFF00" },
  { name: "Gold", hex: "#FFD700" },
  { name: "Mustard", hex: "#FFDB58" },
  { name: "Amber", hex: "#FFBF00" },
  { name: "Lemon", hex: "#FFF44F" },
  { name: "Flax", hex: "#EEDC82" },
  { name: "Butter", hex: "#F6E2B3" },
  { name: "Honey", hex: "#FFC30B" },
  { name: "Sand", hex: "#C2B280" },
  { name: "Sunflower", hex: "#FFC512" },

  // ORANGE
  { name: "Orange", hex: "#FFA500" },
  { name: "Burnt Orange", hex: "#CC5500" },
  { name: "Tangerine", hex: "#F28500" },
  { name: "Coral", hex: "#FF7F50" },
  { name: "Peach", hex: "#FFE5B4" },
  { name: "Apricot", hex: "#FBCEB1" },
  { name: "Rust", hex: "#B7410E" },
  { name: "Pumpkin", hex: "#FF7518" },
  { name: "Terracotta", hex: "#E2725B" },
  { name: "Carrot", hex: "#ED9121" },

  // PURPLE
  { name: "Purple", hex: "#800080" },
  { name: "Violet", hex: "#8F00FF" },
  { name: "Lavender", hex: "#E6E6FA" },
  { name: "Plum", hex: "#8E4585" },
  { name: "Orchid", hex: "#DA70D6" },
  { name: "Lilac", hex: "#C8A2C8" },
  { name: "Mauve", hex: "#E0B0FF" },
  { name: "Amethyst", hex: "#9966CC" },
  { name: "Eggplant", hex: "#614051" },
  { name: "Grape", hex: "#6F2DA8" },

  // BROWNS
  { name: "Brown", hex: "#8B4513" },
  { name: "Dark Brown", hex: "#5C4033" },
  { name: "Light Brown", hex: "#A52A2A" },
  { name: "Chocolate", hex: "#7B3F00" },
  { name: "Coffee", hex: "#6F4E37" },
  { name: "Mocha", hex: "#967969" },
  { name: "Tan", hex: "#D2B48C" },
  { name: "Beige", hex: "#F5F5DC" },
  { name: "Camel", hex: "#C19A6B" },
  { name: "Khaki", hex: "#F0E68C" },
];

function AdminAddProduct() {
  const navigate = useNavigate();
  const categoryRef = useRef(null);
  const tagRef = useRef(null);
  const nameRef = useRef(null);
  const categorySectionRef = useRef(null);
  const mediaSectionRef = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const { lang } = useContext(AdminLanguageContext);
  const [showConfirm, setShowConfirm] = useState(false);
const [showToast, setShowToast] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const [selectedFileNames, setSelectedFileNames] = useState({});

  const [pageLoading, setPageLoading] = useState(true);

  const [slugStatus, setSlugStatus] = useState("idle"); 
// idle | checking | available | taken


const [slugSuggestion, setSlugSuggestion] = useState("");
const [shake, setShake] = useState(false);
const [showValidation, setShowValidation] = useState(false);
const [isUploading, setIsUploading] = useState(false);

const [variants, setVariants] = useState([]);

const [showColorModal, setShowColorModal] = useState(false);
const [selectedColor, setSelectedColor] = useState(null);
const [customHex, setCustomHex] = useState("");
const [customName, setCustomName] = useState("");
const [colorSearch, setColorSearch] = useState("");
const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);

  const inputClass = `
w-full
h-[36px]
px-3
text-[13px]
text-[#202223]
bg-white
border border-gray-500
rounded-[8px]
outline-none
transition
focus:border-[#2c6ecb]
focus:ring-1 focus:ring-[#2c6ecb]/20
`;

const renderProductsIcon = (active) => {
  const Icon = active ? TagOutline : TagSolid;

  return (
    <div className="relative w-[18px] h-[18px] flex items-center justify-center">
      <Icon
        className={`
          w-[18px] h-[18px]
          ${active ? "text-[#202223]" : "text-gray-900"}
          transform scale-x-[-1]
        `}
      />
    </div>
  );
};

  const TEXT = {
    EN: {
      back: "Back to Products",
      title: "Add Product",
      error: "Please fill all required fields.",
      basic: "Basic Info",
      publicId: "Public ID",
      nameEn: "Name (EN)",
      quicklySoldOut: "Quickly sold",
dimensionsComposition: "Dimensions and composition",
dimensionsCompositionEn: "Dimensions and composition (EN)",
dimensionsCompositionAr: "Dimensions and composition (AR)",
      nameAr: "Name (AR)",
      seoSlug: "SEO Slug",
      shortDesc: "Short Descriptions",
      shortDescEn: "Short desc (EN)",
      shortDescAr: "Short desc (AR)",
      desc: "Descriptions",
      descEn: "Description (EN)",
      descAr: "Description (AR)",
      pricing: "Pricing & Flags",
      materialEn: "Material (EN)",
      materialAr: "Material (AR)",
      ageRange: "Age (years)",
      price: "Base Price ($)",
      flags: "Flags",
      bestseller: "Bestseller",
      new: "New",
      sale: "On Sale",
      salePercent: "Sale %",
      discountedPrice: "Discounted Price",
      categories: "Collections",
      tags: "Tags",
      addTag: "Add new tag",
      tagLabelEn: "Tag label (EN)",
      tagLabelAr: "Tag label (AR)",
      createTag: "Create",
      creating: "Creating...",
      images: "Product Images",
      addImage: "Add Image",
      imgUrl: "Image URL",
      altEn: "Alt (EN)",
      altAr: "Alt (AR)",
      sortOrder: "Sort order",
      remove: "Remove",
      cancel: "Cancel",
      save: "Save",
      saving: "Saving...",
      selectAtLeastOneCategory: "Select at least 1 collection.",
    },
    AR: {
      back: "العودة إلى المنتجات",
      materialEn: "الخامة (إنجليزي)",
      materialAr: "الخامة (عربي)",
      quicklySoldOut: "يباع بسرعة",
dimensionsComposition: "الأبعاد والتركيب",
dimensionsCompositionEn: "الأبعاد والتركيب (EN)",
dimensionsCompositionAr: "الأبعاد والتركيب (AR)",
      ageRange: "العمر (سنوات)",
      title: "إضافة منتج",
      error: "يرجى تعبئة جميع الحقول المطلوبة.",
      basic: "المعلومات الأساسية",
      publicId: "المعرّف العام",
      nameEn: "الاسم (إنجليزي)",
      nameAr: "الاسم (عربي)",
      seoSlug: "SEO Slug",
      shortDesc: "وصف قصير",
      shortDescEn: "وصف قصير (إنجليزي)",
      shortDescAr: "وصف قصير (عربي)",
      desc: "الوصف",
      descEn: "الوصف (إنجليزي)",
      descAr: "الوصف (عربي)",
      pricing: "السعر والخصائص",
      price: "السعر الأساسي ($)",
      flags: "الخصائص",
      bestseller: "الأكثر مبيعاً",
      new: "جديد",
      sale: "تخفيض",
      salePercent: "نسبة التخفيض %",
      discountedPrice: "السعر بعد التخفيض",
      categories: "التصنيفات",
      tags: "الوسوم",
      addTag: "إضافة وسم جديد",
      tagLabelEn: "اسم الوسم (إنجليزي)",
      tagLabelAr: "اسم الوسم (عربي)",
      createTag: "إنشاء",
      creating: "جاري الإنشاء...",
      images: "صور المنتج",
      addImage: "إضافة صورة",
      imgUrl: "رابط الصورة",
      altEn: "Alt (إنجليزي)",
      altAr: "Alt (عربي)",
      sortOrder: "ترتيب العرض",
      remove: "حذف",
      cancel: "إلغاء",
      save: "حفظ",
      saving: "جاري الحفظ...",
      selectAtLeastOneCategory: "اختر تصنيف واحد على الأقل.",
    },
  }[lang];

  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // ------- meta lists -------
  const [allCategories, setAllCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);

  const [categorySearch, setCategorySearch] = useState("");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const [tagSearch, setTagSearch] = useState("");
  const [isTagOpen, setIsTagOpen] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showTagToast, setShowTagToast] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  // ------- product fields -------
  const [publicId, setPublicId] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [seoSlug, setSeoSlug] = useState("");

  const [descEn, setDescEn] = useState("");
  const [descAr, setDescAr] = useState("");

  const [dimensionsEn, setDimensionsEn] = useState("");
const [dimensionsAr, setDimensionsAr] = useState("");
  
  // NEW — product details
  const [materialEn, setMaterialEn] = useState("");
  const [materialAr, setMaterialAr] = useState("");

  const [ageRange, setAgeRange] = useState("");

  const [basePrice, setBasePrice] = useState("");
  const [onSale, setOnSale] = useState(false);
  const [salePercent, setSalePercent] = useState(0);
  const [discountInput, setDiscountInput] = useState("");
  const [isBestseller, setIsBestseller] = useState(false);
  const [isNewIn, setIsNewIn] = useState(false);
  const [isQuicklySoldOut, setIsQuicklySoldOut] = useState(false);

  // relations
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // images
  const [images, setImages] = useState([]);

// GLOBAL alt for all images (same as )
const [globalAltEn, setGlobalAltEn] = useState("");
const [globalAltAr, setGlobalAltAr] = useState("");

  // tags accordion + create tag
  const [isTagsOpen, setIsTagsOpen] = useState(false);
  const [newTagEn, setNewTagEn] = useState("");
  const [newTagAr, setNewTagAr] = useState("");
  const [creatingTag, setCreatingTag] = useState(false);

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const [slugTouched, setSlugTouched] = useState(false);

  const getPrimaryCategorySlug = () => {
  if (!selectedCategories.length) return "";

  const found = allCategories.find(
    (c) => Number(c.id) === Number(selectedCategories[0])
  );

  return found?.slug || "";
};

useEffect(() => {
  if (
    publicId ||
    nameEn ||
    nameAr ||
    materialEn ||
    materialAr ||
    ageRange ||
    basePrice ||
    descEn ||
    descAr ||
    images.length > 0 ||
    selectedCategories.length > 0 ||
    selectedTags.length > 0 ||
    variants.length > 0
  ) {
    setIsDirty(true);
  } else {
    setIsDirty(false);
  }
}, [
  publicId,
  nameEn,
  nameAr,
  materialEn,
  materialAr,
  ageRange,
  basePrice,
  descEn,
  descAr,
  images,
  selectedCategories,
  selectedTags,
  variants
]);

useEffect(() => {
  const handleClickOutside = (e) => {
    if (categoryRef.current && !categoryRef.current.contains(e.target)) {
      setIsCategoryOpen(false);
    }

    if (tagRef.current && !tagRef.current.contains(e.target)) {
      setIsTagOpen(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);
  return () => document.removeEventListener("mousedown", handleClickOutside);
}, []);


  useEffect(() => {
  if (!seoSlug.trim()) return;

  const delay = setTimeout(async () => {
    try {
      setSlugStatus("checking");

      const res = await checkAdminSlug(seoSlug);

      if (res.exists) {
        setSlugStatus("taken");

        const suggestion = `${seoSlug}-${Date.now().toString().slice(-4)}`;
        setSlugSuggestion(suggestion);

      } else {
        setSlugStatus("available");
        setSlugSuggestion("");
      }

    } catch (e) {
      console.error(e);
      setSlugStatus("idle");
    }
  }, 500); // debounce

  return () => clearTimeout(delay);

}, [seoSlug]);

useEffect(() => {
  if ((!nameEn.trim() && !selectedCategories.length) || slugTouched) return;

  const categorySlug = getPrimaryCategorySlug();
  const productSlug = slugify(nameEn);

  if (!productSlug) return;

  let finalSlug = productSlug;

  if (categorySlug) {
    finalSlug = `${categorySlug}/${productSlug}`;
  }

  setSeoSlug(finalSlug);

}, [nameEn, selectedCategories, slugTouched]);

useEffect(() => {
  if (!nameEn && !nameAr) return;

  setGlobalAltEn(nameEn || "");
  setGlobalAltAr(nameAr || "");

}, [nameEn, nameAr]);

  // -------- load categories + tags ----------
 useEffect(() => {
  let alive = true;

  setPageLoading(true);

  Promise.all([getAdminCategories(), getAdminTags()])
    .then(([cats, tags]) => {
      if (!alive) return;

      setAllCategories(Array.isArray(cats) ? cats : []);
      setAllTags(Array.isArray(tags) ? tags : []);
    })
    .catch((e) => {
      console.error(e);
      if (alive) setError(e.message || "Error loading meta.");
    })
    .finally(() => {
      if (alive) setPageLoading(false);
    });

  return () => {
    alive = false;
  };
}, []);

  useEffect(() => {
  if (!onSale) return;

  const base = Number(basePrice);
  const discount = Number(discountInput);

  if (!base || !discount) return;

  if (discount >= base) {
    setSalePercent(0);
    return;
  }

  const percent = ((base - discount) / base) * 100;

  setSalePercent(percent.toFixed(2));
}, [discountInput]);

  // Sale UX: if off => percent 0
  useEffect(() => {
    if (!onSale) setSalePercent(0);
  }, [onSale]);

  const scrollToRef = (ref) => {
  ref?.current?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
};

const handleBackClick = () => {
  if (isDirty) {
    setShowLeaveConfirm(true);
  } else {
    navigate("/admin/products");
  }
};

const isValidHex = (hex) => {
  return /^#([0-9A-F]{3}){1,2}$/i.test(hex.trim());
};

const isHexValid = isValidHex(customHex);

  const toggleIdInArray = (arr, idNum) => {
    const has = arr.includes(idNum);
    if (has) return arr.filter((x) => x !== idNum);
    return [...arr, idNum];
  };

  // -------- discounted price preview ----------
  const discountedPrice = useMemo(() => {
  if (!onSale || !basePrice) return "";

  const base = Number(basePrice);

  // PRIORITY: manual discounted input
  if (discountInput !== "") {
    return Number(discountInput).toFixed(2);
  }

  if (!salePercent) return "";

  const d = base - (base * Number(salePercent)) / 100;
  return d.toFixed(2);
}, [basePrice, salePercent, onSale, discountInput]);

  // -------- images helpers ----------
  const addImageRow = () => {
  setImages((prev) => [
    ...(prev || []),
    {
      url: "",
      alt_en: "",
      alt_ar: "",
      color_name: "",
      sort_order: (prev?.length || 0) + 1,
      media_type: "image", // ✅ ADD THIS
    },
  ]);
};


  const removeImageRow = (idx) => {
  setImages((prev) => (prev || []).filter((_, i) => i !== idx));
};

const updateImageRow = (idx, patch) => {
  setImages((prev) =>
    (prev || []).map((img, i) => (i === idx ? { ...img, ...patch } : img))
  );
};

const normalize = (text = "") =>
  text
    .toString()
    .toLowerCase()
    .trim()
    // remove Arabic diacritics
    .replace(/[\u064B-\u0652]/g, "")
    // normalize Arabic letters
    .replace(/أ|إ|آ/g, "ا")
    .replace(/ة/g, "ه");

const filteredCategories = useMemo(() => {
  const search = normalize(categorySearch);

  if (!search) {
    return allCategories.slice(0, 3); // suggested
  }

  return allCategories.filter((c) => {
    const nameEn = normalize(c.name_en);
    const nameAr = normalize(c.name_ar);
    const slug = normalize(c.slug);

    return (
      nameEn.includes(search) ||
      nameAr.includes(search) ||
      slug.includes(search)
    );
  });
}, [categorySearch, allCategories]);

const filteredTags = useMemo(() => {
  const search = tagSearch.trim().toLowerCase();

  if (!search) {
    return allTags.slice(0, 3); // suggested
  }

  return allTags.filter((t) => {
    const labelEn = (t.label_en || "").toLowerCase();
    const labelAr = (t.label_ar || "").toLowerCase();
    const slug = (t.slug || "").toLowerCase();

    return (
      labelEn.includes(search) ||
      labelAr.includes(search) ||
      slug.includes(search)
    );
  });
}, [tagSearch, allTags]);

const groupedVariants = useMemo(() => {
  const map = new Map();

  (variants || []).forEach((v) => {
    const colorName = v.color_name || "Unknown";
    const colorHex = v.color_hex || "#cccccc";
    const key = `${colorName}|||${colorHex}`;

    if (!map.has(key)) {
      map.set(key, {
        color_name: colorName,
        color_hex: colorHex,
        rows: [],
      });
    }

    map.get(key).rows.push({
      size: v.size != null ? String(v.size) : "",
      stock_qty: Number(v.stock_qty || 0),
    });
  });

  return Array.from(map.values());
}, [variants]);

const filteredColors = useMemo(() => {
  const search = colorSearch.trim().toLowerCase();

  if (!search) return COLOR_PALETTE;

  return COLOR_PALETTE.filter((c) =>
    c.name.toLowerCase().includes(search)
  );
}, [colorSearch]);

const toggleTranslate = (active) => {
  if (!active) return "translate-x-0";

  return lang === "AR"
    ? "-translate-x-4" // 🔥 move left in RTL
    : "translate-x-4"; // normal LTR
};

const rebuildFlatVariantsFromGroups = (groups) => {
  const flat = [];

  groups.forEach((g) => {
    if (!g.rows.length) {
      flat.push({
        color_name: g.color_name,
        color_hex: g.color_hex,
        size: "",
        stock_qty: 0,
      });
      return;
    }

    g.rows.forEach((r) => {
      flat.push({
        color_name: g.color_name,
        color_hex: g.color_hex,
        size: String(r.size || "").trim(),
        stock_qty: Number(r.stock_qty || 0),
      });
    });
  });

  setVariants(flat);
};

const addColorGroup = (color) => {
  if (!color) return;

  setVariants((prev) => [
    ...(prev || []),
    {
      color_name: color.name,
      color_hex: color.hex,
      size: "",
      stock_qty: 0,
    },
  ]);
};

const removeColorGroup = (idx) => {
  const groups = groupedVariants.filter((_, i) => i !== idx);
  rebuildFlatVariantsFromGroups(groups);
};

const truncateText = (text = "", max = 30) => {
  if (text.length <= max) return text;
  return text.slice(0, max) + "...";
};

const updateColorGroup = (idx, patch) => {
  const groups = groupedVariants.map((g, i) =>
    i === idx ? { ...g, ...patch } : g
  );
  rebuildFlatVariantsFromGroups(groups);
};

const addSizeRow = (idx) => {
  const groups = groupedVariants.map((g, i) =>
    i === idx
      ? { ...g, rows: [...g.rows, { size: "", stock_qty: 0 }] }
      : g
  );
  rebuildFlatVariantsFromGroups(groups);
};

const updateSizeRow = (idx, rowIdx, patch) => {
  const groups = groupedVariants.map((g, i) => {
    if (i !== idx) return g;
    const newRows = g.rows.map((r, j) =>
      j === rowIdx ? { ...r, ...patch } : r
    );
    return { ...g, rows: newRows };
  });
  rebuildFlatVariantsFromGroups(groups);
};

const removeSizeRow = (idx, rowIdx) => {
  const groups = groupedVariants.map((g, i) =>
    i === idx
      ? { ...g, rows: g.rows.filter((_, j) => j !== rowIdx) }
      : g
  );
  rebuildFlatVariantsFromGroups(groups);
};

  // -------- create new tag ----------
  const handleCreateTag = async () => {
  setError("");

  const en = newTagEn.trim();
  const ar = newTagAr.trim();

  if (!en) {
    setError(
      lang === "AR"
        ? "أدخل اسم الوسم (إنجليزي) على الأقل."
        : "Enter tag label (EN) at least."
    );
    return;
  }

  setCreatingTag(true);
  try {
    const created = await createAdminTag({
      label_en: en,
      label_ar: ar,
    });

    if (created?.id) {
      setAllTags((prev) => [created, ...(prev || [])]);
      setSelectedTags((prev) => toggleIdInArray(prev, Number(created.id)));

      setNewTagEn("");
      setNewTagAr("");

      setShowTagModal(false);
      setIsTagOpen(false);

      setShowTagToast(true);
      setTimeout(() => setShowTagToast(false), 2000);
    }
  } catch (e) {
    console.error(e);
    setError(
  e.message ||
  (lang === "AR" ? "حدث خطأ أثناء إنشاء الوسم" : "Create tag error")
);
  } finally {
    setCreatingTag(false);
  }
};

  // -------- submit ----------
 const handleConfirmSave = async () => {
  setError("");

  const isPublicIdValid = publicId.trim().length > 0;
  const isNameValid = nameEn.trim().length > 0;
  const isMaterialEnValid = materialEn.trim().length > 0;
  const isMaterialArValid = materialAr.trim().length > 0;
  const isAgeValid = true;
  const isPriceValid = basePrice !== "" && Number(basePrice) >= 0;
  const isCategoryValid = selectedCategories.length > 0;
  const isMediaValid = images.length > 0;
  const cleanedVariants = (variants || [])
  .map((v) => ({
    sku: `${publicId}-${v.color_name}-${v.size}`
      .replace(/\s+/g, "-")
      .toUpperCase(),
    color_name: v.color_name,
    color_hex: v.color_hex || "#ccc",
    size: v.size,
    stock_qty: Number(v.stock_qty || 0),
  }))
  .filter((v) => v.size);

   if (
    !isPublicIdValid ||
    !isNameValid ||
    !isMaterialEnValid ||
    !isMaterialArValid ||
!isPriceValid ||
    !isCategoryValid ||
    !isMediaValid
  ) {
    setShowValidation(true);
    setShake(true);
    setTimeout(() => setShake(false), 400);
    setError(TEXT.error);

    if (!isNameValid) {
      scrollToRef(nameRef);
    } else if (!isCategoryValid) {
      scrollToRef(categorySectionRef);
    } else if (!isMediaValid) {
      scrollToRef(mediaSectionRef);
    }

    return;
  }

  if (slugStatus === "taken") {
    setError(
  lang === "AR"
    ? "رابط المنتج مستخدم بالفعل، يرجى تغييره"
    : "SEO slug already exists. Please change it."
);
    return;
  }

  const cleanedImages = (images || [])
    .map((img, idx) => ({
      url: String(img.url || "").trim(),
      alt_en: String(img.alt_en || "").trim(),
      alt_ar: String(img.alt_ar || "").trim(),
      color_name: String(img.color_name || "").trim(),
      sort_order: Number(img.sort_order ?? idx + 1),
      media_type: img.media_type || "image",
    }))
    .filter((img) => img.url);

  if (!cleanedImages.length) {
    setShake(true);
    setTimeout(() => setShake(false), 400);
    setError(lang === "AR" ? "أضف صورة واحدة على الأقل." : "Add at least 1 image.");
    scrollToRef(mediaSectionRef);
    return;
  }

  setSaving(true);
  try {
    await createAdminProduct({
      public_id: publicId.trim(),
      name_en: nameEn.trim(),
      name_ar: nameAr.trim(),
      material_en: materialEn.trim(),
      material_ar: materialAr.trim(),
      age_range: ageRange.trim(),
      short_desc_en: dimensionsEn.trim(),
short_desc_ar: dimensionsAr.trim(),
description_en: descEn.trim(),
description_ar: descAr.trim(),
      base_price: Number(basePrice).toFixed(2),
      on_sale: onSale,
      sale_percent: onSale ? Number(salePercent || 0) : 0,
      is_bestseller: isBestseller,
is_new_in: isNewIn,
is_quickly_sold_out: isQuicklySoldOut,
seo_slug: seoSlug.trim(),
      categories: selectedCategories,
      tags: selectedTags,
      images: cleanedImages,
      variants: cleanedVariants,
    });

    setShowToast(true);
    setTimeout(() => {
      navigate("/admin/products");
    }, 1200);
  } catch (err) {
    console.error(err);
    setError(
  err.message ||
  (lang === "AR" ? "حدث خطأ أثناء الحفظ" : "Save error")
);
  } finally {
    setSaving(false);
  }
};

const handleMultiImageUpload = async (files) => {
  if (!files || !files.length) return;

  setIsUploading(true);

  const startOrder = images.length;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    try {
      const uploaded = await uploadAdminFile(file);

      setImages((prev) => [
        ...prev,
        {
          url: uploaded.url,
          alt_en: globalAltEn,
          alt_ar: globalAltAr,
          color_name: "",
          sort_order: startOrder + i + 1,
          media_type: file.type?.startsWith("video/") ? "video" : "image",
        },
      ]);
    } catch (err) {
      alert(
  err.message ||
  (lang === "AR" ? "فشل رفع الملف" : "Upload failed")
);
    }
  }

  setIsUploading(false);
};

useEffect(() => {
  setImages((prev) =>
    (prev || []).map((img) => ({
      ...img,
      alt_en: globalAltEn,
      alt_ar: globalAltAr,
    }))
  );
}, [globalAltEn, globalAltAr]);

const isNameValid = nameEn.trim().length > 0;
const isPriceValid = basePrice !== "" && Number(basePrice) >= 0;
const isMaterialEnValid = materialEn.trim().length > 0;
const isMaterialArValid = materialAr.trim().length > 0;
const isAgeValid = true;
const isCategoryValid = selectedCategories.length > 0;

const isFormValid =
  publicId.trim() &&
  nameEn.trim() &&
  materialEn.trim() &&
  materialAr.trim() &&
  basePrice !== "" &&
  Number(basePrice) >= 0 &&
  selectedCategories.length > 0 &&
  images.length > 0 &&
  slugStatus !== "taken";

  // ✅ EARLY RETURN (loading state)
if (pageLoading) {
  return (
    <div className="flex bg-[#f1f1f1] min-h-screen">
      <AdminSidebar />

      <div className="flex-1 px-6 py-6 space-y-4">

        {/* HEADER */}
        <div className="h-6 w-40 bg-gray-200 rounded animate-pulse" />

        {/* CARD */}
        <div className="bg-white rounded-xl p-4 space-y-4">
          <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />

          <div className="grid grid-cols-2 gap-4">
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
            <div className="h-10 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>

        {/* MEDIA SKELETON */}
        <div className="bg-white rounded-xl p-4 space-y-3">
          <div className="h-4 w-28 bg-gray-200 rounded animate-pulse" />
          <div className="h-24 bg-gray-200 rounded animate-pulse" />
        </div>

      </div>
    </div>
  );
}

  return (
    <>
    <style>{shakeKeyframes}</style>
    <div className={`flex bg-[#f1f1f1] min-h-screen ${isOpen ? "overflow-hidden" : ""}`}>
      <AdminSidebar isOpen={isOpen} setIsOpen={setIsOpen} />

      <div className={`flex-1 ${lang === "AR" ? "lg:mr-26" : "lg:ml-64"}`}>
  <AdminHeader
  setIsOpen={setIsOpen}
  customCenter={
    <div
      className={`
        hidden sm:block
        w-full
        sm:max-w-[500px]
        max-w-full
        transition-all duration-300
        ${isDirty ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2 pointer-events-none"}
      `}
    >
      <div className="bg-[#2c2c2c] border border-[#3a3a3a] rounded-md px-3 py-2 sm:py-[4px] space-y-2 sm:space-y-0 sm:flex sm:items-center sm:justify-between">

        {/* LEFT */}
        <div className="flex items-center gap-2 text-[13px] text-gray-200 justify-between sm:justify-start">

          <FiAlertCircle className="text-white-400 text-[16px]" />

          <span>
            {lang === "AR" ? "منتج غير محفوظ" : "Unsaved product"}
          </span>

        </div>

        {/* RIGHT */}
        <div className="flex gap-2 sm:gap-1.5 w-full sm:w-auto">

          {/* DISCARD */}
          <button
            type="button"
            onClick={handleBackClick}
            className="
              px-2 py-[2px]
              text-[12px]
              rounded-md
              border border-[#3a3a3a]
              bg-[#1f1f1f]
              hover:bg-[#363636]
              text-gray-200
              transition
            "
          >
            {lang === "AR" ? "إلغاء" : "Discard"}
          </button>

          {/* SAVE */}
          <button
            type="button"
            onClick={() => setShowConfirm(true)}
            disabled={!isFormValid}
            className={`
              px-2 py-[2px]
              text-[12px]
              rounded-md
              font-medium
              transition
              ${
                isFormValid
                  ? "bg-white text-black hover:bg-gray-200"
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
              }
            `}
          >
            {lang === "AR" ? "حفظ" : "Save"}
          </button>

        </div>
      </div>
    </div>
  }
/>


  <main className="px-3 sm:px-6 py-4 pb-20 sm:pb-4 animate-fadeSlide">
    <div className="max-w-[1040px] mx-auto">
          {/* Top bar */}
          <div className="mx-3 mb-2 mt-5 flex items-center gap-2 text-sm">

            {/* PRODUCT ICON BUTTON */}
            <button
              onClick={handleBackClick}
              className="relative flex items-center justify-center w-8 h-8 rounded-md text-[#202223] hover:bg-gray-200 transition"
            >
              <div className="flex items-center justify-center w-6 h-6">
  {renderProductsIcon(true)}
</div>
            </button>

            {/* CHEVRON */}
            <FiChevronRight className="text-gray-400 text-[14px] shrink-0" />

            {/* TITLE */}
            <span className="font-semibold text-[#202223]">
              {TEXT.title}
            </span>

          </div>

          {error && (
              <div className="mb-4 rounded-[12px] border border-[#e4c9c9] bg-[#fff5f5] px-4 py-3 text-sm text-[#8e1f1f] shadow-sm">
                {error}
              </div>
            )}

          {showToast && (
            <div className="fixed top-6 right-6 z-[70] animate-fade-in">
              <div className="min-w-[320px] max-w-[420px] rounded-[14px] border border-[#cde7d8] bg-white shadow-[0_12px_30px_rgba(0,0,0,0.12)] overflow-hidden">
                <div className="flex items-start gap-3 px-4 py-3">
                  <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-[#e3f1df] text-[#29845a] text-sm font-bold">
                    ✓
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#202223]">
                      {lang === "AR" ? "تم إنشاء المنتج" : "Product created"}
                    </div>
                    <div className="mt-0.5 text-sm text-[#6d7175]">
                      {lang === "AR"
                        ? "تم حفظ المنتج بنجاح."
                        : "The product was saved successfully."}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showTagToast && (
            <div className="fixed top-24 right-6 z-[70] animate-fade-in">
              <div className="min-w-[320px] max-w-[420px] rounded-[14px] border border-[#cde7d8] bg-white shadow-[0_12px_30px_rgba(0,0,0,0.12)] overflow-hidden">
                <div className="flex items-start gap-3 px-4 py-3">
                  <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-[#e3f1df] text-[#29845a] text-sm font-bold">
                    ✓
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-[#202223]">
                      {lang === "AR" ? "تم إنشاء الوسم" : "Tag created"}
                    </div>
                    <div className="mt-0.5 text-sm text-[#6d7175]">
                      {lang === "AR"
                        ? "تم إنشاء الوسم وإضافته بنجاح."
                        : "The tag was created and added successfully."}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

{showLeaveConfirm && (
  <div className="fixed inset-0 bg-black/40 z-[9999] flex items-center justify-center">
    <div className="w-full max-w-[420px] bg-white rounded-[18px] border border-[#e1e3e5] shadow-xl p-6">

      <h3 className="text-[18px] font-semibold text-[#202223] mb-2">
        {lang === "EN" ? "Leave without saving?" : "مغادرة بدون حفظ؟"}
      </h3>

      <p className="text-sm text-[#6d7175] mb-6">
        {lang === "EN"
          ? "You have unsaved changes. Are you sure you want to leave?"
          : "لديك تغييرات غير محفوظة. هل أنت متأكد أنك تريد المغادرة؟"}
      </p>

      <div className="flex justify-end gap-3">

        {/* CANCEL */}
        <button
          onClick={() => setShowLeaveConfirm(false)}
          className="h-[38px] px-4 border border-[#c9cccf] rounded-[10px] text-[#202223] hover:bg-[#f1f2f3]"
        >
          {TEXT.cancel}
        </button>

        {/* LEAVE */}
        <button
          onClick={() => navigate("/admin/products")}
          className="h-[38px] px-4 bg-[#202223] text-white rounded-[10px] hover:bg-black"
        >
          {lang === "EN" ? "Leave" : "مغادرة"}
        </button>

      </div>

    </div>
  </div>
)}

          {showConfirm && (
  <div className="fixed inset-0 z-[80] bg-[#202223]/40 backdrop-blur-[2px] flex items-center justify-center px-5 py-4">
    <div className="w-full max-w-[420px] overflow-hidden rounded-[18px] border border-[#e1e3e5] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.18)]">
      <div className="border-b border-[#e1e3e5] px-5 py-4">
        <h3 className="text-[16px] font-semibold text-[#202223]">
          {lang === "AR" ? "تأكيد إنشاء المنتج" : "Create product"}
        </h3>
      </div>

      <div className="px-5 py-4">
        <p className="text-sm leading-6 text-[#6d7175]">
          {lang === "AR"
            ? "هل أنت متأكد أنك تريد إنشاء هذا المنتج؟"
            : "Are you sure you want to create this product?"}
        </p>
      </div>

      <div className="flex items-center justify-end gap-4 border-t border-[#e1e3e5] bg-[#f6f6f7] px-5 py-4">
        <button
          type="button"
          onClick={() => setShowConfirm(false)}
          className="h-[36px] px-4 rounded-[10px] border border-[#c9cccf] bg-white text-[#202223] text-sm font-medium hover:bg-[#f6f6f7]"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          type="button"
          onClick={() => {
            setShowConfirm(false);
            handleConfirmSave();
          }}
          className="h-[36px] px-4 rounded-[10px] bg-[#202223] text-white text-sm font-medium hover:bg-[#111213]"
        >
          {lang === "AR" ? "إنشاء" : "Create"}
        </button>
      </div>
    </div>
  </div>
)}

{showColorModal && (
  <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center p-4">
    <div className="bg-white rounded-2xl w-full max-w-[600px] p-5 shadow-xl">

      <h3 className="text-lg font-semibold mb-3">
  {lang === "AR" ? "اختر لون" : "Select Color"}
</h3>

{/* SEARCH BAR */}
<div className="mb-3">
  <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 bg-white">
    <FaSearch className="text-gray-400 text-[13px]" />

    <input
      value={colorSearch}
      onChange={(e) => setColorSearch(e.target.value)}
      placeholder={lang === "AR" ? "ابحث عن لون..." : "Search colors..."}
      className="flex-1 text-sm outline-none bg-transparent"
    />
  </div>
</div>

      {/* COLOR GRID */}
      <div className="grid grid-cols-5 gap-3 max-h-[300px] overflow-y-auto">
        {filteredColors.length === 0 && (
              <div className="col-span-5 text-center text-xs text-gray-500 py-4">
                {lang === "AR" ? "لا يوجد ألوان" : "No colors found"}
              </div>
            )}
        {filteredColors.map((c) => (
          <button
            key={c.hex}
            onClick={() => setSelectedColor(c)}
            className={`p-2 rounded-lg border text-xs text-center
              ${selectedColor?.hex === c.hex ? "border-black" : "border-gray-200"}
            `}
          >
            <div
              className="w-6 h-6 rounded-full mx-auto mb-1 border"
              style={{ backgroundColor: c.hex }}
            />
            <div>{c.name}</div>
            <div className="text-[10px] text-gray-500">{c.hex}</div>
          </button>
        ))}
      </div>

      {/* CUSTOM COLOR */}
      <div className="mt-4 border-t pt-4">
  <div className="text-sm font-medium mb-3">
    {lang === "AR" ? "لون مخصص" : "Custom Color"}
  </div>

  {/* ROW 1: PICKER + HEX */}
  <div className="flex items-center gap-3 mb-3">

    {/* COLOR PICKER */}
    <div className="relative">
      <input
        type="color"
        value={customHex || "#000000"}
        onChange={(e) => {
          setCustomHex(e.target.value.toUpperCase());
        }}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      />

      <div
        className="w-10 h-10 rounded-lg border border-gray-300 shadow-sm flex items-center justify-center"
        style={{ backgroundColor: customHex || "#ffffff" }}
      >
        {!customHex && (
          <FaPalette className="text-gray-400 text-sm" />
        )}
      </div>
    </div>

    {/* HEX INPUT */}
    <input
      placeholder="#000000"
      value={customHex}
      onChange={(e) =>
        setCustomHex(e.target.value.toUpperCase())
      }
      className={`
        h-[36px] text-sm border rounded px-3 w-[120px]
        ${customHex && !isHexValid ? "border-red-400" : "border-gray-300"}
      `}
    />

  </div>

  {/* ROW 2: NAME */}
  <input
    placeholder={lang === "AR" ? "اسم اللون" : "Color name"}
    value={customName}
    onChange={(e) => setCustomName(e.target.value)}
    className="w-full h-[36px] text-sm border border-gray-300 rounded px-3 mb-3"
  />

  {/* ROW 3: ACTION */}
  <div className="flex justify-end">
    <button
      onClick={() => {
        if (!isHexValid) return;

        setSelectedColor({
          name: customName.trim() || customHex,
          hex: customHex.toUpperCase(),
        });
      }}
      disabled={!isHexValid}
      className={`
        px-4 py-2 rounded-lg text-sm font-medium
        ${isHexValid
          ? "bg-[#202223] text-white hover:bg-black"
          : "bg-gray-200 text-gray-400 cursor-not-allowed"}
      `}
    >
      {lang === "AR" ? "تحديد" : "Set"}
    </button>
  </div>

  {/* ERROR */}
  {customHex && !isHexValid && (
    <p className="text-[11px] text-red-500 mt-2">
      {lang === "AR"
        ? "أدخل كود HEX صالح مثل #000000"
        : "Enter a valid HEX code like #000000"}
    </p>
  )}
</div>

      {/* ACTIONS */}
      <div className="flex justify-end gap-3 mt-5">
        <button
          onClick={() => setShowColorModal(false)}
          className="px-4 py-2 border rounded-lg"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          onClick={() => {
            if (!selectedColor) return;

            if (selectedVariantIndex !== null) {
              updateColorGroup(selectedVariantIndex, {
                color_name: selectedColor.name,
                color_hex: selectedColor.hex,
              });

              setSelectedVariantIndex(null);
            } else {
              addColorGroup(selectedColor);
            }

            setShowColorModal(false);
            setSelectedColor(null);
            setCustomHex("");
            setCustomName("");
          }}
          className="px-4 py-2 bg-black text-white rounded-lg"
        >
          {lang === "AR" ? "تم" : "Done"}
        </button>
      </div>

    </div>
  </div>
)}

{showTagModal && (
  <div className="fixed inset-0 z-[80] bg-[#202223]/40 backdrop-blur-[2px] flex items-center justify-center px-5 py-4">
    <div className="w-full max-w-[520px] overflow-hidden rounded-[18px] border border-[#e1e3e5] bg-white shadow-[0_20px_50px_rgba(0,0,0,0.18)]">
      <div className="border-b border-[#e1e3e5] px-5 py-4">
        <h3 className="text-[16px] font-semibold text-[#202223]">
          {lang === "AR" ? "إضافة وسم جديد" : "Add new tag"}
        </h3>
      </div>

      <div className="px-5 py-5 space-y-4">
        <div>
          <label className="block text-[12px] font-medium text-[#6d7175] mb-1.5">
            {TEXT.tagLabelEn}
          </label>
          <input
            value={newTagEn}
            onChange={(e) => setNewTagEn(e.target.value)}
            className={inputClass}
            placeholder={lang === "AR" ? "مثال: فضة" : "e.g. Silver"}
          />
        </div>

        <div>
          <label className="block text-[12px] font-medium text-[#6d7175] mb-1.5">
            {TEXT.tagLabelAr}
          </label>
          <input
            value={newTagAr}
            onChange={(e) => setNewTagAr(e.target.value)}
            className={inputClass}
            placeholder={lang === "AR" ? "مثال: فضة" : "e.g. فضة"}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 border-t border-[#e1e3e5] bg-[#f6f6f7] px-5 py-4">
        <button
          type="button"
          onClick={() => {
            setShowTagModal(false);
            setNewTagEn("");
            setNewTagAr("");
          }}
          className="h-[36px] px-4 rounded-[10px] border border-[#c9cccf] bg-white text-[#202223] text-sm font-medium hover:bg-[#f6f6f7]"
        >
          {lang === "AR" ? "إلغاء" : "Cancel"}
        </button>

        <button
          type="button"
          onClick={handleCreateTag}
          disabled={creatingTag}
          className="h-[36px] px-4 rounded-[10px] bg-[#202223] text-white text-sm font-medium hover:bg-[#111213] disabled:opacity-60"
        >
          {creatingTag
            ? (lang === "AR" ? "جاري الإنشاء..." : "Creating...")
            : (lang === "AR" ? "إنشاء الوسم" : "Create tag")}
        </button>
      </div>
    </div>
  </div>
)}

          <form
  onSubmit={(e) => {
    e.preventDefault();
    setShowConfirm(true);
  }}
>

  <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-4">
     {/* LEFT COLUMN */}
    <div className="contents xl:block">
  <section className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
    <div className="p-4 space-y-4">
            {/* BASIC INFO */}
            <section>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">              
                  <div ref={nameRef}>
  <div className="mb-1">
    <FieldLabel
      label={TEXT.nameEn}
      hint={
        lang === "AR"
          ? "أدخل اسم المنتج باللغة الإنجليزية (مطلوب)"
          : "Enter the product name in English (required)"
      }
      error={!isNameValid && showValidation ? (lang === "AR" ? "هذا الحقل مطلوب" : "This field is required") : ""}
      valid={isNameValid}
    />
  </div>

  <div className={`${!isNameValid && shake ? shakeClass : ""}`}>
    <input
      value={nameEn}
      onChange={(e) => setNameEn(e.target.value)}
      className={`
        ${inputClass}
        ${!isNameValid && showValidation ? "border-red-400 focus:border-red-500" : ""}
      `}
    />

    {!isNameValid && showValidation && (
      <p className="text-[11px] text-[#d72c0d] mt-1">
        {lang === "AR" ? "هذا الحقل مطلوب" : "This field is required"}
      </p>
    )}
  </div>
</div>
                <div>
                  <div className="mb-1">
  <FieldLabel
    label={TEXT.nameAr}
    hint={
      lang === "AR"
        ? "اسم المنتج بالعربية (اختياري)"
        : "Optional Arabic name for better localization"
    }
  />
</div>
                  <input
                    value={nameAr}
                    onChange={(e) => setNameAr(e.target.value)}
                    className={inputClass}
                  />
                </div>

                <div>
  <div className="mb-1">
    <FieldLabel
      label={TEXT.materialEn}
      hint={
        lang === "AR"
          ? "الخامة باللغة الإنجليزية (مطلوب)"
          : "Material of the product in English (required)"
      }
      error={!materialEn.trim() && showValidation ? (lang === "AR" ? "هذا الحقل مطلوب" : "This field is required") : ""}
valid={materialEn.trim().length > 0}
    />
  </div>

  <div className={`${!materialEn.trim() && shake ? shakeClass : ""}`}>
    <input
      value={materialEn}
      onChange={(e) => setMaterialEn(e.target.value)}
      className={`
        ${inputClass}
        ${!materialEn.trim() && showValidation ? "border-red-400 focus:border-red-500" : ""}
      `}
    />

    {!materialEn.trim() && showValidation && (
      <p className="text-[11px] text-[#d72c0d] mt-1">
        {lang === "AR" ? "هذا الحقل مطلوب" : "This field is required"}
      </p>
    )}
  </div>
</div>

<div>
  <div className="mb-1">
    <FieldLabel
      label={TEXT.materialAr}
      hint={
        lang === "AR"
          ? "الخامة باللغة العربية (مطلوب)"
          : "Material in Arabic (required)"
      }
      error={!materialAr.trim() && showValidation ? (lang === "AR" ? "هذا الحقل مطلوب" : "This field is required") : ""}
      valid={materialAr.trim().length > 0}
    />
  </div>

  <div className={`${!materialAr.trim() && shake ? shakeClass : ""}`}>
    <input
      value={materialAr}
      onChange={(e) => setMaterialAr(e.target.value)}
      className={`
        ${inputClass}
       ${!materialAr.trim() && showValidation ? "border-red-400 focus:border-red-500" : ""}
      `}
    />

    {!materialAr.trim() && showValidation && (
      <p className="text-[11px] text-[#d72c0d] mt-1">
        {lang === "AR" ? "هذا الحقل مطلوب" : "This field is required"}
      </p>
    )}
  </div>
</div>

              </div>
            </section>

            {/* DESCRIPTIONS */}
            <section>
             <div className="mb-1">
  <FieldLabel
    label={TEXT.desc}
    hint={
      lang === "AR"
        ? "وصف تفصيلي للمنتج يظهر في صفحة المنتج"
        : "Detailed product description shown on product page"
    }
    valid={descEn.trim().length > 0 || descAr.trim().length > 0}
  />
</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <textarea
                  rows="4"
                  placeholder={TEXT.descEn}
                  value={descEn}
                  onChange={(e) => setDescEn(e.target.value)}
                  className="
w-full
px-3 py-2
text-sm
text-[#202223]
bg-white
border border-gray-500
outline-none
resize-none
rounded-[8px]
focus:border-[#2c6ecb]
focus:ring-1 focus:ring-[#2c6ecb]/20
"
                />
                <textarea
                  rows="4"
                  placeholder={TEXT.descAr}
                  value={descAr}
                  onChange={(e) => setDescAr(e.target.value)}
                  className="
w-full
px-3 py-2
text-sm
text-[#202223]
bg-white
border border-gray-500
outline-none
resize-none
rounded-[8px]
focus:border-[#2c6ecb]
focus:ring-1 focus:ring-[#2c6ecb]/20
"
                />
              </div>
            </section>

{/* DIMENSIONS AND COMPOSITION */}
<section>
  <div className="mb-1">
    <FieldLabel
      label={TEXT.dimensionsComposition}
      hint={
        lang === "AR"
          ? "أضف تفاصيل الأبعاد والتركيب التي ستظهر في صفحة المنتج"
          : "Add dimensions and composition details shown on the product page"
      }
      valid={dimensionsEn.trim().length > 0 || dimensionsAr.trim().length > 0}
    />
  </div>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <textarea
      rows="3"
      placeholder={TEXT.dimensionsCompositionEn}
      value={dimensionsEn}
      onChange={(e) => setDimensionsEn(e.target.value)}
      className="
        w-full
        px-3 py-2
        text-sm
        text-[#202223]
        bg-white
        border border-gray-500
        outline-none
        resize-none
        rounded-[8px]
        focus:border-[#2c6ecb]
        focus:ring-1 focus:ring-[#2c6ecb]/20
      "
    />

    <textarea
      rows="3"
      placeholder={TEXT.dimensionsCompositionAr}
      value={dimensionsAr}
      onChange={(e) => setDimensionsAr(e.target.value)}
      className="
        w-full
        px-3 py-2
        text-sm
        text-[#202223]
        bg-white
        border border-gray-500
        outline-none
        resize-none
        rounded-[8px]
        focus:border-[#2c6ecb]
        focus:ring-1 focus:ring-[#2c6ecb]/20
      "
    />
  </div>
</section>

            {/* MEDIA SECTION (Shopify style) */}
<section ref={mediaSectionRef}>
  <div className="flex items-center justify-between mb-2">
  <FieldLabel
    label={lang === "AR" ? "الوسائط" : "Media"}
    hint={
      lang === "AR"
        ? "أضف صورة واحدة على الأقل للمنتج. يمكنك رفع صور أو فيديو. (مطلوب)"
        : "Add at least one image for this product. Images or videos are accepted. (required)"
    }
    error={!images.length && showValidation ? (lang === "AR" ? "يجب إضافة صورة واحدة على الأقل" : "At least one image is required") : ""}
    valid={images.length > 0}
  />

  {/* SHOPIFY ADD BUTTON */}
  {images.length >= 3 && (
    <button
      type="button"
      onClick={() =>
        document.getElementById("multi-image-upload").click()
      }
      className="
        flex items-center gap-1
        text-xs font-medium
        px-3 py-1.5
        border border-[#c9cccf]
        rounded-[8px]
        bg-white
        hover:bg-[#f6f6f7]
        transition
      "
    >
      <FaPlus className="text-[10px]" />
      {lang === "AR" ? "إضافة" : "Add"}
    </button>
  )}
</div>

  {/* HIDDEN INPUT */}
  <input
    id="multi-image-upload"
    type="file"
    accept="image/*,video/*"
    multiple
    className="hidden"
    onChange={(e) => {
      const files = Array.from(e.target.files || []);
      handleMultiImageUpload(files);
      e.target.value = "";
    }}
  />
{images.length === 0 && (
  <>
  {/* DROPZONE */}
  <div
  onClick={() => {
    if (isUploading) return;
    document.getElementById("multi-image-upload").click();
  }}
  className={`
    border-2 border-dashed border-gray-400 
    rounded-[12px]
    bg-white
    transition
    cursor-pointer
    flex flex-col items-center justify-center
    text-center
    px-6 py-7
    ${isUploading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#f1f2f3]"}
  `}
>
    {/* ICON */}
    <FaImage className="text-[28px] text-gray-400 mb-3" />

    {/* ACTIONS */}
    <div className="flex items-center gap-2 mb-2">
      <span className="px-3 py-1.5 bg-white border border-gray-500 rounded-[8px] text-sm font-medium hover:bg-gray-100">
        {lang === "AR" ? "رفع جديد" : "Upload new"}
      </span>
    </div>

    {/* SUBTEXT */}
    <p className="text-xs text-gray-500">
      {lang === "AR"
        ? "يمكنك رفع صور، فيديو أو ملفات ثلاثية الأبعاد"
        : "Accepts images, videos, or 3D models"}
    </p>
  </div>
</>
)}
  {images.length === 0 && showValidation && (
  <p className="text-[11px] text-[#d72c0d] mt-2">
    {lang === "AR"
      ? "يجب إضافة صورة واحدة على الأقل"
      : "At least one image is required"}
  </p>
)}

</section>

{isUploading && (
  <p className="text-xs text-gray-500 mt-2">
    {lang === "AR" ? "جارٍ الرفع..." : "Uploading..."}
  </p>
)}

  {images.length > 0 && (
  <div className="mt-4 overflow-x-auto">
    <div className="flex gap-3 pb-2 min-w-max">
      
      {/* IMAGE CARDS */}
      {images.map((img, idx) => (
        <div
  key={idx}
  className="w-[170px] shrink-0 border border-[#e1e3e5] rounded-[12px] bg-white overflow-hidden"
>
  {/* PREVIEW */}
  <div className="h-[100px] bg-[#f6f6f7] flex items-center justify-center">
    {img.url ? (
      img.media_type === "video" ? (
        <video src={img.url} className="h-full object-contain" />
      ) : (
        <img src={img.url} alt="" className="h-full object-contain" />
      )
    ) : (
      <FaImage className="text-gray-300 text-xl" />
    )}
  </div>

  {/* CONTENT */}
  <div className="p-2 space-y-2">

    {/* SORT */}
    <div>
      <div className="text-[10px] text-gray-500 mb-1">
       {lang === "AR" ? "ترتيب العرض" : "Sort"}
      </div>
      <input
        type="number"
        value={img.sort_order}
        onChange={(e) =>
          updateImageRow(idx, {
            sort_order: Number(e.target.value || 0),
          })
        }
        className="w-full h-[28px] text-xs border rounded px-2"
      />
    </div>

    {/* COLOR */}
    <div>
      <div className="text-[10px] text-gray-500 mb-1">
        {lang === "AR" ? "اللون" : "Color"}
      </div>
      <input
        value={img.color_name || ""}
        onChange={(e) =>
          updateImageRow(idx, { color_name: e.target.value })
        }
        placeholder={lang === "AR" ? "أسود..." : "Black..."}
        className="w-full h-[28px] text-xs border rounded px-2"
      />
    </div>

    {/* REMOVE */}
    <button
      type="button"
      onClick={() => removeImageRow(idx)}
      className="w-full text-[11px] text-red-600 hover:underline pt-1"
    >
      {TEXT.remove}
    </button>
  </div>
</div>
      ))}

      {/* ADD MORE CARD */}
      <div
  onClick={() => {
    if (isUploading) return;
    document.getElementById("multi-image-upload").click();
  }}
  className={`
    w-[120px] h-[110px]
    shrink-0
    border-2 border-dashed border-gray-400
    rounded-[12px]
    bg-gray-100
    flex items-center justify-center
    cursor-pointer
    transition
    mt-12
    ${isUploading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#f6f6f7]"}
  `}
>
        {isUploading ? (
          <div className="w-6 h-6 border-2 border-gray-300 border-t-black rounded-full animate-spin" />
        ) : (
          <FaPlus className="text-gray-400 text-xl" />
        )}
      </div>
    </div>
  </div>
)}
    </div>
  </section>

  {/* VARIANTS CARD */}
<section className="mt-3 bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
  <div className="p-4 space-y-4">

    <div className="flex items-center justify-between">
      <div className="mb-1">
  <FieldLabel
    label={lang === "AR" ? "المتغيرات" : "Variants"}
    hint={
      lang === "AR"
        ? "أضف ألوان ومقاسات للمنتج مع الكمية لكل خيار"
        : "Add colors and sizes for this product with stock quantities"
    }
    valid={variants.length > 0}
  />
</div>

      {groupedVariants.length > 0 && (
  <button
    type="button"
    onClick={() => setShowColorModal(true)}
    className="px-3 py-1.5 text-xs rounded-[8px] bg-[#202223] text-white flex items-center gap-1"
  >
    <FaPlus className="text-[10px]" />
    {lang === "AR" ? "إضافة لون" : "Add Color"}
  </button>
)}
    </div>

    {groupedVariants.length === 0 ? (
  <button
    type="button"
    onClick={() => setShowColorModal(true)}
    className="
      w-full
      border border-dashed border-gray-400
      rounded-[12px]
      bg-white
      hover:bg-[#f6f6f7]
      transition
      py-1
      flex flex-col items-center justify-center
      gap-2
    "
  >
    <div className="mt-1 w-6 h-6 rounded-full border border-gray-400 flex items-center justify-center">
      <FaPlus className="text-gray-500 text-sm" />
    </div>

    <span className="text-[12px] text-gray-600 font-medium">
      {lang === "AR" ? "إضافة لون ومقاس" : "Add size and color"}
    </span>
  </button>
) : (
      groupedVariants.map((g, idx) => (
        <div key={idx} className="border rounded-[10px] p-3 space-y-3">

<div className="flex justify-end">
  <button
    type="button"
    onClick={() => removeColorGroup(idx)}
    className="text-gray-400 hover:text-red-600 transition"
  >
    <FaTrash className="text-sm" />
  </button>
</div>

          {/* COLOR */}
          <div className="flex items-center gap-2">

  {/* COLOR NAME */}
 <div className="flex items-center gap-2">

  {/* COLOR DISPLAY */}
  <div
    onClick={() => {
      setSelectedColor({
        name: g.color_name,
        hex: g.color_hex,
      });

      setShowColorModal(true);

      // store index to update later
      setSelectedVariantIndex(idx);
    }}
    className="flex items-center gap-2 cursor-pointer group"
  >
    {/* CIRCLE */}
    <div
      className="w-6 h-6 rounded-full border transition group-hover:scale-105"
      style={{ backgroundColor: g.color_hex }}
    />

    {/* NAME */}
    <span className="text-sm font-medium text-gray-900">
      {g.color_name}
    </span>

    {/* HEX */}
    <span className="text-xs text-gray-400">
      {g.color_hex}
    </span>
  </div>
</div>
</div>

          {/* SIZES */}
          {g.rows.map((r, rowIdx) => (
            <div key={rowIdx} className="flex gap-2">

              <input
                value={r.size}
                onChange={(e) =>
                  updateSizeRow(idx, rowIdx, { size: e.target.value })
                }
                placeholder={lang === "AR" ? "المقاس" : "Size"}
                className="h-[30px] text-xs border rounded px-2"
              />

              <input
                type="number"
                value={r.stock_qty}
                onChange={(e) =>
                  updateSizeRow(idx, rowIdx, {
                    stock_qty: Number(e.target.value || 0),
                  })
                }
                placeholder={lang === "AR" ? "الكمية" : "Qty"}
                className="h-[30px] text-xs border rounded px-2"
              />

              {rowIdx !== 0 && (
  <button
    type="button"
    onClick={() => removeSizeRow(idx, rowIdx)}
    className="text-red-600 text-xs"
  >
    ✕
  </button>
)}
            </div>
          ))}

          <button
            type="button"
            onClick={() => addSizeRow(idx)}
            className="text-xs text-blue-600"
          >
            + {lang === "AR" ? "إضافة مقاس" : "Add Size"}
          </button>
        </div>
      ))
    )}
  </div>
</section>

  {/* SECOND CARD — PRICING */}
<section className="mt-3 bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
  
  <div className="px-5 py-4">
    <div className="mb-3">
  <FieldLabel
    label={TEXT.pricing}
    hint={
      lang === "AR"
        ? "حدد سعر المنتج ويمكنك إضافة تخفيض"
        : "Set the product price and optionally add a discount"
    }
    error={!isPriceValid && showValidation ? (lang === "AR" ? "هذا الحقل مطلوب" : "This field is required") : ""}
    valid={isPriceValid}
  />
</div>

    {/* PRICING + FLAGS */}
            <section>
             <div className="space-y-4">

  {/* BASE PRICE */}
  <div>
    <label className="text-xs text-gray-500 mb-1 block">
      {TEXT.price}
    </label>

    <input
      type="number"
      value={basePrice}
      onChange={(e) => setBasePrice(e.target.value)}
      className={inputClass}
      placeholder="0.00"
    />
  </div>

  {/* ON SALE */}
  <div className="flex items-center justify-between pt-2 border-t">
    <span className="text-sm font-medium">
      {TEXT.sale}
    </span>

    <div
      onClick={() => {
        setOnSale(!onSale);
        if (!onSale) {
          setDiscountInput("");
        }
      }}
      className={`w-10 h-6 flex items-center rounded-full p-[2px] cursor-pointer transition
        ${onSale ? "bg-[#202223]" : "bg-[#8c9196]"}`}
    >
      <div
        className={`w-5 h-5 bg-white rounded-full shadow-md transform transition
          ${toggleTranslate(onSale)}`}
      />
    </div>
  </div>

  {/* SALE BOX */}
  {onSale && (
    <div className="bg-[#f6f6f7] border border-[#e1e3e5] rounded-[10px] p-3 space-y-3">

      <div className="grid grid-cols-2 gap-3">

        {/* SALE % */}
        <div>
          <label className="text-[11px] text-gray-500 mb-1 block">
            {TEXT.salePercent}
          </label>

          <input
            type="number"
            value={salePercent}
            onChange={(e) => {
              setSalePercent(e.target.value);
              setDiscountInput(""); // reset manual
            }}
            className={inputClass}
          />
        </div>

        {/* DISCOUNTED PRICE (EDITABLE 🔥) */}
        <div>
          <label className="text-[11px] text-gray-500 mb-1 block">
            {TEXT.discountedPrice}
          </label>

          <input
            type="number"
            value={discountInput || discountedPrice}
            onChange={(e) => setDiscountInput(e.target.value)}
            className={inputClass}
          />
        </div>

      </div>

      {/* PREVIEW LINE (Shopify style 🔥) */}
      {discountedPrice && (
        <div className="text-xs text-gray-600 pt-1">
          <span className="line-through mr-2">
            ${Number(basePrice || 0).toFixed(2)}
          </span>

          <span className="font-semibold text-black">
            ${discountedPrice}
          </span>
        </div>
      )}

    </div>
  )}

</div>
            </section>
    
  </div>
</section>
      </div>

      {/* RIGHT COLUMN */}
     <div className="space-y-3 contents xl:block">
      {/* PRODUCT IDENTIFIERS */}
<section className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
  <div className="p-4 space-y-3">

    <div className="space-y-2">

      {/* PUBLIC ID */}
      <div>
        <div className="mb-1">
  <FieldLabel
  label={TEXT.publicId}
  hint={
    lang === "AR"
      ? "معرف فريد للاستخدام الداخلي (مطلوب)"
      : "Unique identifier for internal use (required)"
  }
  error={!publicId.trim() && showValidation ? (lang === "AR" ? "هذا الحقل مطلوب" : "This field is required") : ""}
  valid={publicId.trim().length > 0}
/>
</div>
        <input
  value={publicId}
  onChange={(e) => setPublicId(e.target.value)}
  className={`
    ${inputClass}
    ${!publicId.trim() && showValidation ? "border-red-400 focus:border-red-500" : ""}
  `}
/>

{!publicId.trim() && showValidation && (
  <p className="text-[11px] text-[#d72c0d] mt-1">
    {lang === "AR" ? "هذا الحقل مطلوب" : "This field is required"}
  </p>
)}
      </div>

      {/* SEO SLUG */}
      <div>
  <div className="mb-1">
  <FieldLabel
  label={TEXT.seoSlug}
  hint={
    lang === "AR"
      ? "رابط المنتج في الموقع (مطلوب)"
      : "URL-friendly product identifier (required)"
  }
  error={
    showValidation && !seoSlug.trim()
      ? (lang === "AR" ? "هذا الحقل مطلوب" : "This field is required")
      : slugStatus === "taken"
      ? (lang === "AR" ? "هذا الرابط مستخدم بالفعل" : "This slug already exists")
      : ""
  }
  valid={seoSlug.trim().length > 0 && slugStatus !== "taken"}
/>
</div>

  <input
  value={seoSlug}
  onChange={(e) => {
    setSeoSlug(e.target.value);
    setSlugTouched(true);
  }}
  className={`
    ${inputClass}
    ${(showValidation && !seoSlug.trim()) || slugStatus === "taken"
      ? "border-red-400 focus:border-red-500"
      : ""}
  `}
/>

<div className="text-[11px] text-gray-400 mt-1">
  URL: /product/{seoSlug}
</div>

  {/* STATUS */}
  <div className="mt-1 text-xs">

    {slugStatus === "checking" && (
  <span className="text-gray-500">
    {lang === "AR" ? "جاري التحقق..." : "Checking..."}
  </span>
)}

{slugStatus === "available" && (
  <span className="text-green-600 font-medium">
    ✓ {lang === "AR" ? "متاح" : "Available"}
  </span>
)}

{slugStatus === "taken" && (
  <div className="text-red-600 font-medium">
    ✕ {lang === "AR" ? "الرابط مستخدم" : "Slug already exists"}
  </div>
)}

  </div>

  {/* SUGGESTION */}
  {slugStatus === "taken" && slugSuggestion && (
    <button
      type="button"
      onClick={() => {
        setSeoSlug(slugSuggestion);
        setSlugTouched(true);
      }}
      className="mt-1 text-xs text-blue-600 hover:underline"
    >
      {lang === "AR" ? "استخدم المقترح:" : "Use suggestion:"} {slugSuggestion}
    </button>
  )}

</div>

    </div>

  </div>
</section>

{/* CATEGORIES */}
<section
  ref={categoryRef}
  className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
>
  <div ref={categorySectionRef} className="p-4 space-y-3">

    <div className="mb-1">
  <FieldLabel
    label={TEXT.categories}
    hint={
      lang === "AR"
        ? "اختر التصنيف الذي ينتمي إليه المنتج. يمكن اختيار أكثر من واحد. هذا هو القسم أو المجموعة التي سيظهر المنتج تحتها. (مطلوب)"
        : "Select the collection this product belongs to. You can choose more than one. This controls where the product appears on the website. (required)"
    }
    error={!isCategoryValid && showValidation ? TEXT.selectAtLeastOneCategory : ""}
    valid={isCategoryValid}
  />
</div>

    {/* SEARCH INPUT */}
    <div className="relative">
      <div className="flex items-center gap-2 border border-gray-500 rounded-[10px] px-3 h-[36px] bg-white">
        
        <FaSearch className="text-gray-400 text-[13px]" />

        <input
          value={categorySearch}
          onChange={(e) => {
            setCategorySearch(e.target.value);
            setIsCategoryOpen(true);
          }}
          onFocus={() => setIsCategoryOpen(true)}
          placeholder={lang === "AR" ? "ابحث عن تصنيف..." : "Search Collections..."}
          className="flex-1 text-sm outline-none bg-transparent"
        />
      </div>

      {/* OVERLAY */}
      {isCategoryOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#c9cccf] rounded-xl shadow-md max-h-56 overflow-y-auto">
          
          {allCategories.length === 0 ? (
  <div className="px-3 py-3 text-sm text-gray-500 text-center">
    {lang === "AR" ? "لا توجد تصنيفات" : "No collections available"}
  </div>
) : filteredCategories.length === 0 ? (
  <div className="px-3 py-3 text-sm text-gray-500 text-center">
    {lang === "AR" ? "لا توجد نتائج" : "No results found"}
  </div>
) : (
  filteredCategories.map((c) => {
    const cid = Number(c.id);
    const checked = selectedCategories.includes(cid);

    return (
      <label
  key={c.id}
  onClick={() => {
    const isAlreadySelected = selectedCategories.includes(cid);

    setSelectedCategories((prev) =>
      toggleIdInArray(prev, cid)
    );

    if (!isAlreadySelected) {
      setIsCategoryOpen(false);
    }
  }}
  className={`
    flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-all duration-150
    ${checked ? "bg-[#f1f2f3]" : "hover:bg-gray-100"}
  `}
>
  <div
    className={`
      w-5 h-5 rounded-[6px] border flex items-center justify-center transition
      ${checked
        ? "bg-[#202223] border-[#202223]"
        : "bg-white border-[#c9cccf] hover:border-[#202223]"}
    `}
  >
    {checked && <FaCheck className="text-white text-[10px]" />}
  </div>

  <span>
    {lang === "AR"
      ? c.name_ar || c.name_en
      : c.name_en || c.name_ar}
  </span>
</label>
    );
  })
)}
        </div>
      )}
    </div>

    {/* SELECTED */}
    {selectedCategories.length > 0 && (
      <div className="flex flex-wrap gap-2 pt-1">
        {selectedCategories.map((cid) => {
          const found = allCategories.find(
            (c) => Number(c.id) === Number(cid)
          );

          return (
            <div
              key={cid}
              className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#f1f2f3] text-xs"
            >
              <span>
                {lang === "AR"
                  ? found?.name_ar || found?.name_en
                  : found?.name_en || found?.name_ar}
              </span>

              <button
                type="button"
                onClick={() =>
                  setSelectedCategories((prev) =>
                    prev.filter((id) => id !== cid)
                  )
                }
                className="text-gray-500 hover:text-black"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    )}

{!isCategoryValid && showValidation && (
  <p className="text-[11px] text-[#d72c0d]">
    {TEXT.selectAtLeastOneCategory}
  </p>
)}
  </div>
</section>

           {/* TAGS */}
<section
  ref={tagRef}
  className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
>
  <div className="p-4 space-y-3">

    <div className="mb-1">
  <FieldLabel
    label={TEXT.tags}
    hint={
      lang === "AR"
        ? "تُستخدم الوسوم لتحسين البحث داخل الموقع. يمكن اختيار أكثر من وسم. (اختياري)"
        : "Tags are used to improve product search on the website. You can select more than one tag. (optional)"
    }
    valid={selectedTags.length > 0}
  />
</div>

    <div className="relative">
      <div className="flex items-center gap-2 border border-gray-500 rounded-[10px] px-3 h-[36px] bg-white">
        <FaSearch className="text-gray-400 text-[13px]" />

        <input
          value={tagSearch}
          onChange={(e) => {
            setTagSearch(e.target.value);
            setIsTagOpen(true);
          }}
          onFocus={() => setIsTagOpen(true)}
          placeholder={lang === "AR" ? "ابحث عن وسم..." : "Search tags..."}
          className="flex-1 text-sm outline-none bg-transparent"
        />
      </div>

      {isTagOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-[#c9cccf] rounded-xl shadow-md max-h-56 overflow-y-auto">

          <button
            type="button"
            onClick={() => {
              setShowTagModal(true);
              setIsTagOpen(false);
            }}
            className="w-full flex items-center gap-2 px-3 py-2 bg-[#f6f6f7] hover:bg-[#eef0f1] text-[#202223] text-sm border-b border-[#e1e3e5]"
          >
            <FaPlus className="text-[12px]" />
            <span>{TEXT.addTag}</span>
          </button>

         {allTags.length === 0 ? (
  <div className="px-3 py-3 text-sm text-gray-500 text-center">
    {lang === "AR" ? "لا توجد وسوم" : "No tags available"}
  </div>
) : filteredTags.length === 0 ? (
  <div className="px-3 py-3 text-sm text-gray-500 text-center">
    {lang === "AR" ? "لا توجد نتائج" : "No results found"}
  </div>
) : (
            filteredTags.map((t) => {
              const tid = Number(t.id);
              const checked = selectedTags.includes(tid);

              return (
                <label
  key={t.id}
  onClick={() => {
    const isAlreadySelected = selectedTags.includes(tid);

    setSelectedTags((prev) =>
      toggleIdInArray(prev, tid)
    );

    if (!isAlreadySelected) {
      setIsTagOpen(false);
    }
  }}
  className={`
  flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-all duration-150
  ${checked ? "bg-[#f1f2f3]" : "hover:bg-gray-100"}
`}
>
                  <div
                    className={`
                      w-5 h-5 rounded-[6px] border flex items-center justify-center cursor-pointer transition
                      ${checked
                        ? "bg-[#202223] border-[#202223]"
                        : "bg-white border-[#c9cccf] hover:border-[#202223]"}
                    `}
                  >
                    {checked && <FaCheck className="text-white text-[10px]" />}
                  </div>

                  {(() => {
                      const fullText =
                        lang === "AR"
                          ? t.label_ar || t.label_en || t.slug
                          : t.label_en || t.slug;

                      return (
                        <span title={fullText}>
                          {truncateText(fullText)}
                        </span>
                      );
                    })()}
                </label>
              );
            })
          )}
        </div>
      )}
    </div>

    {selectedTags.length > 0 && (
      <div className="flex flex-wrap gap-2 pt-1">
        {selectedTags.map((tid) => {
          const found = allTags.find(
            (t) => Number(t.id) === Number(tid)
          );

          return (
            <div
              key={tid}
              className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#f1f2f3] text-xs"
            >
              {(() => {
                const fullText =
                  lang === "AR"
                    ? found?.label_ar || found?.label_en || found?.slug
                    : found?.label_en || found?.slug;

                return (
                  <span title={fullText}>
                    {truncateText(fullText)}
                  </span>
                );
              })()}

              <button
                type="button"
                onClick={() =>
                  setSelectedTags((prev) =>
                    prev.filter((id) => id !== tid)
                  )
                }
                className="text-gray-500 hover:text-black"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    )}

  </div>
</section>

<section className="bg-white border border-[#e3e3e3] rounded-[12px] shadow-[0_1px_2px_rgba(0,0,0,0.06)]">
  <div className="p-4 space-y-3">

   <div className="mb-2">
  <FieldLabel
    label={lang === "AR" ? "الحالة" : "Status"}
    hint={
  lang === "AR"
    ? "حدد إذا كان المنتج جديد أو الأكثر مبيعًا أو يباع بسرعة"
    : "Mark this product as new, bestseller, or quickly sold out"
}
valid={isBestseller || isNewIn || isQuicklySoldOut}
  />
</div>

    {/* BESTSELLER */}
    <div className="flex items-center justify-between text-sm">
      <span>{TEXT.bestseller}</span>

      <div
        onClick={() => setIsBestseller(!isBestseller)}
        className={`w-10 h-6 flex items-center rounded-full p-[2px] cursor-pointer transition
          ${isBestseller ? "bg-[#202223]" : "bg-[#8c9196]"}`}
      >
        <div
          className={`w-5 h-5 bg-white rounded-full shadow-md transform transition
            ${toggleTranslate(isBestseller)}`}
        />
      </div>
    </div>

    {/* NEW */}
    <div className="flex items-center justify-between text-sm">
      <span>{TEXT.new}</span>

      <div
        onClick={() => setIsNewIn(!isNewIn)}
        className={`w-10 h-6 flex items-center rounded-full p-[2px] cursor-pointer transition
          ${isNewIn ? "bg-[#202223]" : "bg-[#8c9196]"}`}
      >
        <div
          className={`w-5 h-5 bg-white rounded-full shadow-md transform transition
            ${toggleTranslate(isNewIn)}`}
        />
      </div>
    </div>

{/* QUICKLY SOLD OUT */}
<div className="flex items-center justify-between text-sm">
  <span>{TEXT.quicklySoldOut}</span>

  <div
    onClick={() => setIsQuicklySoldOut(!isQuicklySoldOut)}
    className={`w-10 h-6 flex items-center rounded-full p-[2px] cursor-pointer transition
      ${isQuicklySoldOut ? "bg-[#202223]" : "bg-[#8c9196]"}`}
  >
    <div
      className={`w-5 h-5 bg-white rounded-full shadow-md transform transition
        ${toggleTranslate(isQuicklySoldOut)}`}
    />
  </div>
</div>

  </div>
</section>
          </div>
           </div>
          </form>
          <div className="flex justify-end mt-6">
  <button
    type="submit"
    onClick={() => setShowConfirm(true)}
    disabled={saving}
    className={`
      flex items-center gap-2
      px-3 h-[30px]
      rounded-[10px]
      text-sm font-medium
      text-white
      bg-[#202223]

      shadow-[0_2px_0_0_#000]
      hover:bg-black
      hover:translate-y-[1px]
      hover:shadow-[0_1px_0_0_#000]

      active:translate-y-[2px]
      active:shadow-none

      transition-all duration-150

      disabled:opacity-50 disabled:cursor-not-allowed
    `}
  >
    {saving ? TEXT.saving : TEXT.save}
  </button>
</div>
         </div>
         {/* MOBILE ACTION BAR */}
<div className="sm:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-[#e1e3e5] px-3 py-2">

  <div className="flex items-center justify-between gap-2">

    {/* LEFT - STATUS */}
    <div className="flex items-center gap-2 text-[12px] text-gray-600">
      <FiAlertCircle className="text-[14px]" />
      <span>
        {lang === "AR" ? "غير محفوظ" : "Unsaved"}
      </span>
    </div>

    {/* RIGHT - ACTIONS */}
    <div className="flex items-center gap-2">

      {/* DISCARD */}
      <button
        onClick={handleBackClick}
        className="
          px-3 h-[32px]
          text-[12px]
          rounded-[8px]
          border border-[#c9cccf]
          bg-white
          text-[#202223]
          hover:bg-[#f1f2f3]
        "
      >
        {lang === "AR" ? "إلغاء" : "Discard"}
      </button>

      {/* SAVE */}
      <button
        onClick={() => setShowConfirm(true)}
        disabled={!isFormValid}
        className={`
          px-3 h-[32px]
          text-[12px]
          rounded-[8px]
          font-medium
          transition
          ${
            isFormValid
              ? "bg-[#202223] text-white hover:bg-black"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }
        `}
      >
        {lang === "AR" ? "حفظ" : "Save"}
      </button>

    </div>
  </div>
</div>
        </main>
      </div>
    </div>
    </>
  );
}

export default AdminAddProduct;